import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Row, Col, Button } from "react-bootstrap";
import { getFarmList, archivedFarm, unArchivedFarm } from "../../actions/farm";
import { getOrganizationList } from "../../actions/organization";
import {
  isToday,
  usePermission,
  searchWord,
  sortBy,
  groupBy,
} from "../../helpers/common";
import {
  DataTable,
  CardContainer,
  Heading5,
  Icon,
  ButtonGroupToggle,
} from "../widgets";
import FarmForm from "./FarmForm";
import { SubNav } from "../layout/submenu";
import FarmRow from "./FarmRow";
import Notification from "../widgets/Notification";
import { LOAD_MODE, LOAD_TYPE } from "../../constants";

const FarmList = (props) => {
  const dispatch = useDispatch();
  const isRequesting = useSelector((state) => state.farm.isRequesting);
  const isOrganizationRequesting = useSelector(
    (state) => state.organization.isOrganizationRequesting
  );
  const farmList = useSelector((state) => state.farm.farmList);
  const organizationList = useSelector(
    (state) => state.organization.organizationList
  );
  const { range } = useSelector((state) => state.analyze.searchParams);
  const [showModal, setShowModal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [currentFarmUuid, setCurrentFarmUuid] = useState("");
  const [currentFarmName, setCurrentFarmName] = useState("");
  const [currentFarmTimezone, setCurrentFarmTimezone] = useState("");
  const [currentFarmLoadType, setCurrentFarmLoadType] = useState(
    LOAD_TYPE.AUTOMATIC
  );
  const [currentFarmLoadMode, setCurrentFarmLoadMode] = useState(
    LOAD_MODE.FIELD_CENTRIC
  );
  const [isEditFarmName, setIsEditFarmName] = useState(false);
  const [selectedViewType, setSelectedViewType] = useState(1);
  const [showNotificationConfig, setShowNotificationConfig] = useState(false);
  const searchInput = React.createRef();

  const tabs = [
    { value: 1, label: "Active" },
    { value: 2, label: "Archived" },
  ];

  useEffect(() => {
    searchInput.current.focus();
    dispatch(getFarmList());
    if (organizationList.length === 0) dispatch(getOrganizationList());
  }, []);

  const allow = usePermission();
  const isAllowCreate = allow("farm", "create");

  const groupFarmOrganization = (farmList, key) => {
    const groupedList = groupBy(key)(farmList);
    let rowIndex = 0;
    Object.keys(groupedList).map((key) =>
      groupedList[key].map((v) => (v.rowIndex = ++rowIndex))
    );
    return groupedList;
  };

  const filteredList = useMemo(() => {
    let filteredData =
      searchText.trim().length > 0
        ? farmList.filter(
            (item) =>
              searchWord(item.name, searchText) ||
              searchWord(item.timezone, searchText)
          )
        : farmList;
    filteredData =
      selectedViewType === 1
        ? filteredData.filter((item) => item.status_id === 1)
        : filteredData.filter((item) => item.status_id === 5);
    filteredData = sortBy(filteredData, "organization_name");
    return groupFarmOrganization(filteredData, "organization_uuid");
  }, [farmList, searchText, selectedViewType]);

  const handleAddFarm = () => {
    setShowModal(true);
    setIsEditFarmName(false);
  };

  const handleFarmEdit = (farmId, farmName, loadType, loadMode, timezone) => {
    setShowModal(true);
    setIsEditFarmName(true);
    setCurrentFarmLoadType(loadType ? loadType : LOAD_TYPE.AUTOMATIC);
    setCurrentFarmLoadMode(loadMode ? loadMode : LOAD_MODE.FIELD_CENTRIC);
    setCurrentFarmUuid(farmId);
    setCurrentFarmName(farmName);
    setCurrentFarmTimezone(timezone);
  };

  const handleOnHide = () => {
    setShowModal(false);
    setIsEditFarmName(false);
  };

  const handleArchived = (farmId) => {
    const cb = () => {
      dispatch(getFarmList(false));
    };
    dispatch(archivedFarm({ farmId }, cb));
  };

  const handleUnArchived = (farmId) => {
    const cb = () => {
      dispatch(getFarmList(false));
    };
    dispatch(unArchivedFarm({ farmId }, cb));
  };

  const handleNotificationConfig = (value, farmId) => {
    setShowNotificationConfig(value);
    if (farmId) setCurrentFarmUuid(farmId);
  };

  return (
    <>
      <CardContainer lg={10} md={10}>
        <Card className="card-list farm-list-view">
          <Card.Header>
            <Card.Title>
              <Heading5>Farm List</Heading5>
              {isAllowCreate && (
                <Button
                  variant="success"
                  size="sm"
                  className="float-right"
                  onClick={handleAddFarm}
                >
                  <Icon className="fa fa-plus"></Icon> Add Farm
                </Button>
              )}
            </Card.Title>
          </Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Col lg={4} md={4} sm={4}>
                <SubNav.SearchBox
                  ref={searchInput}
                  placeholder="Search for name, timezone"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onClear={() => setSearchText("")}
                />
              </Col>
              <Col>
                <ButtonGroupToggle
                  size="sm"
                  options={tabs}
                  value={selectedViewType}
                  onSelect={(type) => setSelectedViewType(type)}
                  className="btn-group-toggle"
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <DataTable>
                  <DataTable.Header>
                    <DataTable.Row>
                      <DataTable.Cell style={{ width: "40px" }}>
                        #
                      </DataTable.Cell>
                      <DataTable.Cell>Name</DataTable.Cell>
                      <DataTable.Cell width="480px">Timezone</DataTable.Cell>
                      <DataTable.Cell width="120px">Load Type</DataTable.Cell>
                      <DataTable.Cell width="100px">Access Code</DataTable.Cell>
                      <DataTable.Cell width="140px">Action</DataTable.Cell>
                      <DataTable.Cell width="100px">
                        Notification Config
                      </DataTable.Cell>
                    </DataTable.Row>
                  </DataTable.Header>
                  <DataTable.Body>
                    {Object.keys(filteredList).map((organizationId) => (
                      <FarmRow
                        key={organizationId}
                        organizationId={organizationId}
                        farm={filteredList[organizationId]}
                        organizationList={organizationList}
                        handleFarmEdit={handleFarmEdit}
                        handleArchived={handleArchived}
                        handleUnArchived={handleUnArchived}
                        selectedViewType={selectedViewType}
                        showNotificationConfig={showNotificationConfig}
                        handleNotificationConfig={handleNotificationConfig}
                      />
                    ))}
                  </DataTable.Body>
                </DataTable>
                {isRequesting || isOrganizationRequesting ? (
                  <DataTable.Loading />
                ) : (
                  Object.keys(filteredList).length === 0 && (
                    <DataTable.RecordsNotFound />
                  )
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </CardContainer>
      {isToday(range) && showModal && (
        <FarmForm
          onHide={handleOnHide}
          isEditFarmName={isEditFarmName}
          currentFarmName={currentFarmName}
          currentFarmTimezone={currentFarmTimezone}
          currentFarmLoadType={currentFarmLoadType}
          currentFarmLoadMode={currentFarmLoadMode}
          currentFarmUuid={currentFarmUuid}
        />
      )}
      {showNotificationConfig && (
        <Notification
          show={showNotificationConfig}
          onHide={handleNotificationConfig}
          farmId={currentFarmUuid}
        />
      )}
    </>
  );
};

export default FarmList;
